import {
  createFileRoute,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { useAuth } from '../contexts/AuthContext';
import {
  useLegacyAuthGatewayRq,
  useLegacyAuthGatewayFetch,
} from '@penta/penta-api';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, Modal } from '@penta/penta-shared';
import { toSafeRedirectUrl } from '../utils/safeRedirect';
import styles from './login.module.scss';

export const Route = createFileRoute('/login')({
  component: Login,
});

// Codes are single-use: once sent to the exchange they are dead, but the URL
// carrying them would survive in history and across reloads, and Back/reload
// would replay it. On success the whole callback entry is replaced by the
// app (navigate replace: true); this strip covers the failure path, where no
// navigation follows — the retry reload then starts a clean login round
// instead of replaying the dead code. Only safe with no navigation in flight:
// TanStack Router patches replaceState and re-runs the login effect off it
// (the error-state guard keeps that re-run inert).
const stripCodeFromHistory = () => {
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search,
  );
};

export function Login() {
  const { t } = useTranslation();
  const { login: setToken, logout, isAuthenticated } = useAuth();
  const legacyApi = useLegacyAuthGatewayRq();
  const loginMutation = legacyApi.useMutation('post', '/v1/token');
  const { mutateAsync: loginMutateAsync } = loginMutation;
  const fetchClient = useLegacyAuthGatewayFetch();
  const navigate = useNavigate();
  const location = useLocation(); // location object

  // Track processed auth code to prevent double execution in Strict Mode or re-renders
  // Initialize as undefined to distinguish from "no code" (null)
  const processedCodeRef = useRef<string | null | undefined>(undefined);
  const loginCompletedRef = useRef(false);

  const [status, setStatus] = useState<string>(t('login.loading.initializing'));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleLogin = async () => {
      try {
        if (loginCompletedRef.current) {
          return;
        }

        // While the error modal is up, stripCodeFromHistory() has already
        // changed location.hash and re-runs this effect — without this guard
        // the empty-code branch would fire and redirect the user away from
        // the error into a new login round. Recovery goes through the modal's
        // explicit reload instead.
        if (error) {
          return;
        }

        // Parse code from hash #code=xyz
        // Prioritize router location, fallback to window for robustness
        // The hash MUST be checked before the isAuthenticated shortcut: a code
        // in the URL means the legacy IB just completed a new login round, so
        // any token in sessionStorage may have been invalidated by a logout
        // there and cannot be trusted.
        const hashStr = location.hash || window.location.hash || '';

        const hashParams = new URLSearchParams(hashStr.replace(/^#/, ''));
        const code = hashParams.get('code');

        // Skip if we already processed this exact code (or lack thereof)
        // This prevents double-execution in Strict Mode and redirect loops
        if (processedCodeRef.current === code) {
          // Kód se zásadně neloguje — je to živý jednorázový přihlašovací údaj
          // a konzole končí v podpoře, screen recordingu i breadcrumbs Sentry.
          return;
        }
        processedCodeRef.current = code;

        if (code) {
          // Discard the (possibly stale) stored token before the exchange so
          // a failed exchange cannot leave us "authenticated" with a token
          // the backend already invalidated.
          logout();

          setStatus(t('login.loading.verifying'));

          // The hash must stay in the URL until the exchange settles — the
          // processedCodeRef guard relies on it to short-circuit re-runs
          // triggered by the logout() state change while the POST is in
          // flight. Stripping earlier would let a re-run fall into the
          // empty-code branch and redirect away mid-exchange.
          try {
            const data = await loginMutateAsync({ body: { code } });

            if (data && data.token) {
              loginCompletedRef.current = true;
              setToken(data.token);
              // replace: the /login#code callback entry must not survive in
              // history — Back would replay the consumed one-time code
              navigate({ to: '/', replace: true });
            } else {
              stripCodeFromHistory();
              setError(
                t('login.error.failedPrefix', {
                  message: t('login.error.validationFailed'),
                }),
              );
            }
          } catch (apiError) {
            stripCodeFromHistory();
            console.error('Login failed', apiError);
            const msg =
              (apiError as { message?: string }).message ||
              t('login.error.validationFailed');
            setError(t('login.error.failedPrefix', { message: msg }));
          }
        } else if (isAuthenticated) {
          loginCompletedRef.current = true;
          // replace keeps /login out of history so Back doesn't bounce here
          navigate({ to: '/', replace: true });
        } else {
          setStatus(t('login.loading.connecting'));

          // Call API without code to get redirect URL
          // We expect 401, so we check response status. We keep fetchClient here
          // because we need direct access to the response headers for the 401 redirect mechanic.
          const { response } = await fetchClient.POST('/v1/token', {
            body: { code: '' },
          });

          if (response.status === 401) {
            const authHeader = response.headers.get('WWW-Authenticate');
            if (authHeader) {
              // Parse "Url https://..."
              // Example: Url https://ibankdev.privatbanka.sk/ExtranetRedirect/Pages/Login.aspx
              const match = authHeader.match(/^Url\s+(.+)$/i);
              if (match && match[1]) {
                // Cíl určuje odpověď serveru. Blokuje se jen nepovolené
                // schéma — `javascript:` by se ze same-origin skriptu vykonalo.
                // Cizí doména se jen loguje (viz safeRedirect).
                const target = toSafeRedirectUrl(match[1]);
                if (!target) {
                  console.error(
                    '[Login] Odmítnut cíl přesměrování s nepovoleným schématem',
                  );
                  setError(t('login.error.invalidHeader'));
                  return;
                }
                window.location.href = target.toString();
                return;
              }
            }
            console.error(
              'Missing or invalid WWW-Authenticate header',
              authHeader,
            );
            setError(t('login.error.invalidHeader'));
          } else {
            setError(
              t('login.error.unexpectedStatus', { status: response.status }),
            );
          }
        }
      } catch (err: unknown) {
        console.error(err);
        setError(err instanceof Error ? err.message : t('login.error.unknown'));
      }
    };

    handleLogin();
    // Re-run if hash changes
  }, [
    location.hash,
    fetchClient,
    setToken,
    logout,
    navigate,
    t,
    isAuthenticated,
    loginMutateAsync,
    error,
  ]);

  if (error) {
    return (
      <Modal
        open
        onOpenChange={() => {
          processedCodeRef.current = undefined;
          window.location.reload();
        }}
        title={t('login.error.title')}
        description={error}
        data-testid="login-error-modal"
      >
        <button
          onClick={() => {
            processedCodeRef.current = undefined;
            window.location.reload();
          }}
          className={styles['retryButton']}
        >
          {t('login.retry')}
        </button>
      </Modal>
    );
  }

  return (
    <Modal
      open
      closeOnInteractOutside={false}
      closeOnEscape={false}
      aria-label={status}
      data-testid="login-loading"
    >
      <Spinner size="lg" />
    </Modal>
  );
}
